import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Bare axios call to the refresh endpoint — must NOT go through `api` (its
// interceptors would recurse). Phase 3.3.
const refreshAuthToken = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('no token');
  const res = await axios.post(
    `${API_BASE_URL}/auth/refresh/`,
    {},
    { headers: { Authorization: `Token ${token}` } }
  );
  localStorage.setItem('token', res.data.token);
  return res.data.token;
};

const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

// Handle 401 errors (unauthorized) and network errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle network errors (ERR_EMPTY_RESPONSE, etc.)
    if (!error.response) {
      console.error('Network error:', error.message);
      // If it's a network error and we have a token, it might be expired or invalid
      if (error.code === 'ERR_NETWORK' || error.message.includes('EMPTY_RESPONSE')) {
        const token = localStorage.getItem('token');
        if (token) {
          console.warn('Network error with token present, clearing auth data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Don't redirect on network errors as it might be a temporary connection issue
        }
      }
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      const original = error.config || {};
      const code = error.response.data?.code;
      // Phase 3.3: a merely-expired token can be rotated once, transparently.
      if (code === 'token_expired' && !original._retry) {
        original._retry = true;
        try {
          const fresh = await refreshAuthToken();
          original.headers = { ...original.headers, Authorization: `Token ${fresh}` };
          return api(original);
        } catch (e) {
          forceLogout();
          return Promise.reject(error);
        }
      }
      // Any other 401 (bad token, reauth_required, refresh failed) → sign out.
      forceLogout();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password, mfaCode) =>
    api.post('/users/login/', {
      username,
      password,
      ...(mfaCode ? { mfa_code: mfaCode } : {}),
    }),
  getCurrentUser: () => api.get('/users/me/'),
  // Phase 3.3: server-side token revocation. Best-effort — the caller clears
  // local state regardless of the outcome.
  logout: () => api.post('/users/logout/'),
};

// Students API
export const studentsAPI = {
  list: (params) => api.get('/students/', { params }),
  get: (id) => api.get(`/students/${id}/`),
  create: (data) => api.post('/students/', data),
  update: (id, data) => api.patch(`/students/${id}/`, data),
  delete: (id) => api.delete(`/students/${id}/`),
  assignAgent: (id, agentId) => api.post(`/students/${id}/assign_agent/`, { agent_id: agentId }),
  updateStatus: (id, status) => api.post(`/students/${id}/update_status/`, { status }),
  markCommissionPaid: (id) => api.post(`/students/${id}/mark_commission_paid/`),
  statistics: () => api.get('/students/statistics/'),
  actionBoard: (agentId) => api.get('/students/action-board/', { params: agentId ? { agent: agentId } : {} }),
  pinQuadrant: (id, quadrant) => api.post(`/students/${id}/pin-quadrant/`, { quadrant }),
  exportData: (id) => api.get(`/students/${id}/export-data/`),
  erase: (id) => api.post(`/students/${id}/erase/`, { confirm: true }),
  issueLoginSetup: (id) => api.post(`/students/${id}/issue-login-setup/`),
  programMatches: (id) => api.get(`/students/${id}/program-matches/`),
  getCountries: () => api.get('/countries/'),
  getTurkishUniversities: () => api.get('/turkish-universities/'),
  // Alumni API
  listAlumni: (params) => api.get('/alumni/', { params }),
  getAlumni: (id) => api.get(`/alumni/${id}/`),
  createAlumni: (data) => api.post('/alumni/', data),
  updateAlumni: (id, data) => api.patch(`/alumni/${id}/`, data),
  deleteAlumni: (id) => api.delete(`/alumni/${id}/`),
  // Ban / unban
  banStudent: (id, reason) => api.post(`/students/${id}/ban/`, { reason }),
  unbanStudent: (id) => api.post(`/students/${id}/unban/`),
  // Transfer to alumni
  transferToAlumni: (id) => api.post(`/students/${id}/transfer-to-alumni/`),
  // Deleted / restore
  listDeleted: (params) => api.get('/students/deleted/', { params }),
  restore: (id) => api.post(`/students/${id}/restore/`),
  // Partner progress tracking — document checklist (staff read via the
  // student itself; write via checklistItemsAPI below)
  checklist: (id) => api.get(`/students/${id}/checklist/`),
  checklistPdf: (id) => api.get(`/students/${id}/checklist/pdf/`, { responseType: 'blob' }),
  // Document submission tracker — visa profile (Q7/Q9) + public link (Q2)
  getVisaProfile: (id) => api.get(`/students/${id}/visa-profile/`),
  updateVisaProfile: (id, data) => api.patch(`/students/${id}/visa-profile/`, data),
  getDenklikProfile: (id) => api.get(`/students/${id}/denklik-profile/`),
  updateDenklikProfile: (id, data) => api.patch(`/students/${id}/denklik-profile/`, data),
  getChecklistLink: (id) => api.get(`/students/${id}/checklist-link/`),
  createChecklistLink: (id) => api.post(`/students/${id}/checklist-link/`),
  regenerateChecklistLink: (id) => api.post(`/students/${id}/checklist-link/regenerate/`),
  revokeChecklistLink: (id) => api.post(`/students/${id}/checklist-link/revoke/`),
};

// Referrer-consent links (partner-progress-tracking design, Q1)
export const consentRequestsAPI = {
  list: (studentId) => api.get('/consent-requests/', { params: { student_id: studentId } }),
  sendLink: (studentId, respondentRole) => api.post('/consent-requests/', {
    student: studentId, ...(respondentRole ? { respondent_role: respondentRole } : {}),
  }),
  recordVerbal: (studentId, respondedName) => api.post('/consent-requests/record-verbal/', {
    student_id: studentId, responded_name: respondedName || '',
  }),
  withdraw: (studentId) => api.post('/consent-requests/withdraw/', { student_id: studentId }),
};

// Document checklist item review (partner-progress-tracking design, Q3/Q4)
export const checklistItemsAPI = {
  accept: (itemId) => api.post(`/checklist-items/${itemId}/accept/`),
  reject: (itemId, note) => api.post(`/checklist-items/${itemId}/reject/`, { note }),
  markNotNeeded: (itemId) => api.post(`/checklist-items/${itemId}/mark-not-needed/`),
  // Staff attaches a file directly — for items with no partner/student
  // upload path (document submission tracker, e.g. the plane-ticket item).
  upload: (itemId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/checklist-items/${itemId}/upload/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Payments ledger (document submission tracker, Q5)
export const paymentRecordsAPI = {
  list: (studentId) => api.get('/payment-records/', { params: { student_id: studentId } }),
  create: (data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') form.append(k, v); });
    return api.post('/payment-records/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // data: { invoice } or { not_for_invoice_reason } for a VisionExploit-fee entry.
  accept: (id, data = {}) => api.post(`/payment-records/${id}/accept/`, data),
  reject: (id, note) => api.post(`/payment-records/${id}/reject/`, { note }),
};

// Country Education Reference (document tracker follow-up, 2026-09-29) —
// sourced from Türkiye's MEB Denklik Kılavuzu.
export const countryEducationAPI = {
  list: (search) => api.get('/country-education-systems/', { params: search ? { search } : {} }),
  get: (id) => api.get(`/country-education-systems/${id}/`),
  create: (data) => api.post('/country-education-systems/', data),
  update: (id, data) => api.patch(`/country-education-systems/${id}/`, data),
  delete: (id) => api.delete(`/country-education-systems/${id}/`),
};

// App Logins API
export const appLoginsAPI = {
  list: () => api.get('/app-logins/'),
  block: (id, reason) => api.post(`/app-logins/${id}/block/`, { reason }),
  unblock: (id) => api.post(`/app-logins/${id}/unblock/`),
};

// Documents API
export const documentsAPI = {
  list: (params) => api.get('/documents/', { params }),
  get: (id) => api.get(`/documents/${id}/`),
  create: (data) => {
    const formData = new FormData();
    formData.append('student', data.student);
    formData.append('document_type', data.document_type);
    formData.append('file', data.file);
    return api.post('/documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  update: (id, data) => {
    const formData = new FormData();
    if (data.student) formData.append('student', data.student);
    if (data.document_type) formData.append('document_type', data.document_type);
    if (data.file) formData.append('file', data.file);
    return api.patch(`/documents/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (id) => api.delete(`/documents/${id}/`),
  byStudent: (studentId) => api.get(`/documents/by_student/?student_id=${studentId}`),
  /** Download document file (authenticated). Returns blob; use responseType in options. */
  download: (id) =>
    api.get(`/documents/${id}/download/`, { responseType: 'blob' }),
  /** Mark a document as staff-verified */
  verify: (id, note = '') => api.post(`/documents/${id}/verify/`, { note }),
  /** Public hash verification (no auth). Pass the SHA-256 hex string. */
  publicVerify: (fileHash) => api.get(`/documents/verify/${fileHash}/`),
};

// System Activity / Audit Log API (Chairman/Admin/CEO/COO only)
export const activityAPI = {
  list: (params) => api.get('/activities/', { params }),
};

// Data-subject requests (KVKK Art. 11 register)
export const dataRequestsAPI = {
  list: (params) => api.get('/data-requests/', { params }),
  create: (data) => api.post('/data-requests/', data),
  update: (id, data) => api.patch(`/data-requests/${id}/`, data),
};

// Users API
export const usersAPI = {
  list: (params) => api.get('/users/', { params }),
  get: (id) => api.get(`/users/${id}/`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.patch(`/users/${id}/`, data),
  delete: (id) => api.delete(`/users/${id}/`),
  agents: () => api.get('/users/agents/'),
  permanentEmployees: () => api.get('/users/permanent_employees/'),
  agentStatistics: () => api.get('/users/agent_statistics/'),
  agentDetailStatistics: (id) => api.get(`/users/${id}/agent_detail_statistics/`),
  // Partner Analytics (card grid over agents/institutions/hospitals) — generalizes
  // agentStatistics across all 4 verticals instead of Student-only.
  partnerStatistics: () => api.get('/users/partner_statistics/'),
  // Chairman-only offboarding tool — rotate a staff member's login credentials
  // (and revoke their standing session) when they leave the company.
  resetCredentials: (id, data) => api.post(`/users/${id}/reset-credentials/`, data),
  // The acting user's own record + signature upload (for billing signatures).
  me: () => api.get('/users/me/'),
  // "Cost of time" input (VEC_MS_FINANCE_SEEDING_PLAN.md Phase 4) — called
  // by useActivityHeartbeat, not directly by page components.
  heartbeat: () => api.post('/users/heartbeat/'),
  uploadSignature: (file) => {
    const fd = new FormData();
    fd.append('signature_file', file);
    return api.patch('/users/me/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Agencies API (Chairman/Admin/CEO/COO can view; only Chairman/Admin can suspend/reinstate)
export const agenciesAPI = {
  list: () => api.get('/agencies/'),
  get: (id) => api.get(`/agencies/${id}/`),
  create: (data) => api.post('/agencies/', data),
  update: (id, data) => api.patch(`/agencies/${id}/`, data),
  delete: (id) => api.delete(`/agencies/${id}/`),
  suspend: (id, reason) => api.post(`/agencies/${id}/suspend/`, { reason }),
  reinstate: (id) => api.post(`/agencies/${id}/reinstate/`),
};

// Growth-stack access delegation (CEO/COO grant a Permanent Employee access to
// Agencies + Sub-Agent Applications)
export const growthDelegationsAPI = {
  list: () => api.get('/growth-delegations/'),
  create: (delegatedUserId) => api.post('/growth-delegations/', { delegated_user: delegatedUserId, is_active: true }),
  setActive: (id, isActive) => api.patch(`/growth-delegations/${id}/`, { is_active: isActive }),
  remove: (id) => api.delete(`/growth-delegations/${id}/`),
  myAccess: () => api.get('/growth-delegations/my_access/'),
};

// Stand-in covers (Growth Access → Stand-ins): the Chairman/CEO/COO start and end them; mine() is
// what the signed-in person covers today.
export const roleCoversAPI = {
  list: () => api.get('/role-covers/'),
  create: (data) => api.post('/role-covers/', data),
  end: (id) => api.post(`/role-covers/${id}/end/`),
  candidates: (role) => api.get('/role-covers/candidates/', { params: { role } }),
  mine: () => api.get('/role-covers/mine/'),
};

// Sub-Agent Applications API (create is public — used by the WEBSITE apply form)
export const subAgentApplicationsAPI = {
  list: () => api.get('/sub-agent-applications/'),
  approve: (id, commissionSplitPercent) => api.post(`/sub-agent-applications/${id}/approve/`, { commission_split_percent: commissionSplitPercent }),
  reject: (id, reason) => api.post(`/sub-agent-applications/${id}/reject/`, { reason }),
};

// Commission Records API (Chairman/Admin/CEO/COO)
export const commissionsAPI = {
  list: (params) => api.get('/commission-records/', { params }),
  update: (id, data) => api.patch(`/commission-records/${id}/`, data),
  markReceived: (id) => api.post(`/commission-records/${id}/mark_received/`),
  markAgencyPaid: (id) => api.post(`/commission-records/${id}/mark_agency_paid/`),
  delete: (id) => api.delete(`/commission-records/${id}/`),
  agencyReport: () => api.get('/commission-records/agency_report/'),
};

// University Partnerships API (Chairman/Admin/CEO/COO)
export const partnershipsAPI = {
  list: () => api.get('/universities/partnerships/'),
  create: (data) => api.post('/universities/partnerships/', data),
  update: (id, data) => api.patch(`/universities/partnerships/${id}/`, data),
  delete: (id) => api.delete(`/universities/partnerships/${id}/`),
  volumeReport: () => api.get('/universities/partnerships/volume_report/'),
  universities: () => api.get('/universities/list/'),
};

// System Settings API
export const settingsAPI = {
  get: () => api.get('/settings/'),
  update: (data) => api.patch('/settings/', data),
  killSessions: (keepCurrent = true) => api.post('/settings/kill-sessions/', { keep_current: keepCurrent }),
};

// Board Report API
export const boardReportAPI = {
  get: (params) => api.get('/board-report/', { params }),
  getConfig: () => api.get('/board-report-config/'),
  updateConfig: (data) => api.patch('/board-report-config/', data),
  // CMO's scoped view — just marketing_budget/marketing_salaries, not the whole board-report config
  marketingConfig: {
    get: () => api.get('/marketing-config/'),
    update: (data) => api.patch('/marketing-config/', data),
  },
};

// Notifications API
export const notificationsAPI = {
  list: (params) => api.get('/notifications/', { params }),
  unreadCount: () => api.get('/notifications/unread_count/'),
  markRead: (id) => api.post(`/notifications/${id}/mark_read/`),
  markAllRead: () => api.post('/notifications/mark_all_read/'),
};

// Deals API (CRM pipeline)
export const dealsAPI = {
  list: (params) => api.get('/deals/', { params }),
  get: (id) => api.get(`/deals/${id}/`),
  create: (data) => api.post('/deals/', data),
  update: (id, data) => api.patch(`/deals/${id}/`, data),
  delete: (id) => api.delete(`/deals/${id}/`),
  moveStage: (id, stage, note, lostReason) =>
    api.post(`/deals/${id}/move-stage/`, { stage, note, lost_reason: lostReason }),
  pipelineSummary: () => api.get('/deals/pipeline_summary/'),
};

// Quotations API (quote-to-invoice, linked from Deals)
export const quotationsAPI = {
  list: (params) => api.get('/quotations/', { params }),
  get: (id) => api.get(`/quotations/${id}/`),
  create: (data) => api.post('/quotations/', data),
  update: (id, data) => api.patch(`/quotations/${id}/`, data),
  delete: (id) => api.delete(`/quotations/${id}/`),
  markSent: (id) => api.post(`/quotations/${id}/mark-sent/`),
  markAccepted: (id) => api.post(`/quotations/${id}/mark-accepted/`),
  markDeclined: (id) => api.post(`/quotations/${id}/mark-declined/`),
  linkInvoice: (id, invoiceId) => api.post(`/quotations/${id}/link-invoice/`, { invoice_id: invoiceId }),
  issue: (id) => api.post(`/quotations/${id}/issue/`),
  void: (id, reason) => api.post(`/quotations/${id}/void/`, { reason }),
  createInvoice: (id) => api.post(`/quotations/${id}/create-invoice/`),
  recordPayment: (id, data) => api.post(`/quotations/${id}/record-payment/`, data),
  newDeadline: (id, data) => api.post(`/quotations/${id}/new-deadline/`, data),
  pdf: (id) => api.get(`/quotations/${id}/pdf/`, { responseType: 'blob' }),
  applySignature: (id, formData) =>
    api.post(`/quotations/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Credit / refund notes and payment plans against an issued invoice, and the
// per-client statement of account. See projects/billing-documents/plan.md.
export const creditNotesAPI = {
  list: (params) => api.get('/credit-notes/', { params }),
  get: (id) => api.get(`/credit-notes/${id}/`),
  create: (data) => api.post('/credit-notes/', data),
  update: (id, data) => api.patch(`/credit-notes/${id}/`, data),
  issue: (id) => api.post(`/credit-notes/${id}/issue/`),
  void: (id, reason) => api.post(`/credit-notes/${id}/void/`, { reason }),
  markRefunded: (id, data) => api.post(`/credit-notes/${id}/mark-refunded/`, data),
  pdf: (id) => api.get(`/credit-notes/${id}/pdf/`, { responseType: 'blob' }),
  applySignature: (id, formData) =>
    api.post(`/credit-notes/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const paymentPlansAPI = {
  list: (params) => api.get('/payment-plans/', { params }),
  get: (id) => api.get(`/payment-plans/${id}/`),
  create: (data) => api.post('/payment-plans/', data),
  update: (id, data) => api.patch(`/payment-plans/${id}/`, data),
  issue: (id) => api.post(`/payment-plans/${id}/issue/`),
  void: (id, reason) => api.post(`/payment-plans/${id}/void/`, { reason }),
  pdf: (id) => api.get(`/payment-plans/${id}/pdf/`, { responseType: 'blob' }),
  applySignature: (id, formData) =>
    api.post(`/payment-plans/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const statementsAPI = {
  get: (studentId) => api.get(`/billing/statements/${studentId}/`),
  pdf: (studentId) => api.get(`/billing/statements/${studentId}/pdf/`, { responseType: 'blob' }),
};

export const budgetForecastsAPI = {
  list: (params) => api.get('/budget-forecasts/', { params }),
  get: (id) => api.get(`/budget-forecasts/${id}/`),
  create: (data) => api.post('/budget-forecasts/', data),
  update: (id, data) => api.patch(`/budget-forecasts/${id}/`, data),
  issue: (id) => api.post(`/budget-forecasts/${id}/issue/`),
  void: (id, reason) => api.post(`/budget-forecasts/${id}/void/`, { reason }),
  pdf: (id) => api.get(`/budget-forecasts/${id}/pdf/`, { responseType: 'blob' }),
  applySignature: (id, formData) =>
    api.post(`/budget-forecasts/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  lookups: () => api.get('/budget-forecasts-lookups/'),
};

export const serviceAgreementsAPI = {
  list: (params) => api.get('/service-agreements/', { params }),
  get: (id) => api.get(`/service-agreements/${id}/`),
  create: (data) => api.post('/service-agreements/', data),
  update: (id, data) => api.patch(`/service-agreements/${id}/`, data),
  issue: (id) => api.post(`/service-agreements/${id}/issue/`),
  void: (id, reason) => api.post(`/service-agreements/${id}/void/`, { reason }),
  markLawyerApproved: (id) => api.post(`/service-agreements/${id}/mark-lawyer-approved/`),
  pdf: (id) => api.get(`/service-agreements/${id}/pdf/`, { responseType: 'blob' }),
  applySignature: (id, formData) =>
    api.post(`/service-agreements/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Send a document out for the client/sponsor/guardian to sign themselves —
// available on quotationsAPI, paymentPlansAPI and serviceAgreementsAPI (each
// document's own viewset exposes the same two actions via _DocumentActions).
export const signingAPI = {
  send: (kind, id) => api.post(`/${kind}/${id}/send-for-signature/`),
  list: (kind, id) => api.get(`/${kind}/${id}/signature-requests/`),
};

// Billing documents — service price book, the branded template, receipts, and
// client-facing invoice PDFs. See projects/billing-documents/plan.md.
export const billingAPI = {
  // The CFO's Payments desk (founder rule, 19 Oct 2026: only the CFO records a payment).
  // Unpaid invoices + expiring quotations — the Outstanding tab (deals/reminders.py).
  outstanding: () => api.get('/billing/outstanding/'),
  // One client's billing documents, chained (founder, 24 Oct 2026).
  clientFile: (studentId) => api.get(`/billing/client-file/${studentId}/`),
  paymentDesk: {
    queue: () => api.get('/billing/payment-desk/queue/'),
    clients: (q) => api.get('/billing/payment-desk/clients/', { params: { q } }),
  },
  priceItems: {
    list: (params) => api.get('/service-price-items/', { params }),
    create: (data) => api.post('/service-price-items/', data),
    update: (id, data) => api.patch(`/service-price-items/${id}/`, data),
    delete: (id) => api.delete(`/service-price-items/${id}/`),
  },
  template: {
    get: () => api.get('/document-template/'),
    // multipart so the logo can be uploaded alongside the text fields
    update: (formData) =>
      api.post('/document-template/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  },
  receipts: {
    list: (params) => api.get('/receipts/', { params }),
    get: (id) => api.get(`/receipts/${id}/`),
    create: (data) => api.post('/receipts/', data),
    update: (id, data) => api.patch(`/receipts/${id}/`, data),
    delete: (id) => api.delete(`/receipts/${id}/`),
    issue: (id) => api.post(`/receipts/${id}/issue/`),
    // Which invoice(s) a receipt pays: the client's open invoices, the unmatched
    // queue, and link / split / unlink an issued receipt (CFO or Chairman).
    openInvoices: (student, currency) => api.get('/receipts/open-invoices/', { params: { student, currency } }),
    unmatched: () => api.get('/receipts/unmatched/'),
    match: (id, data) => api.post(`/receipts/${id}/match/`, data),
    pdf: (id) => api.get(`/receipts/${id}/pdf/`, { responseType: 'blob' }),
    applySignature: (id, formData) =>
      api.post(`/receipts/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  },
  invoiceDoc: {
    get: (id) => api.get(`/billing/invoices/${id}/`),
    pdf: (id) => api.get(`/billing/invoices/${id}/pdf/`, { responseType: 'blob' }),
    applySignature: (id, formData) =>
      api.post(`/billing/invoices/${id}/apply-signature/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  },
};

// Trigger a browser download for a blob response from any of the *.pdf() calls.
export const downloadBlob = (response, filename) => {
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Meetings API
export const meetingsAPI = {
  list: (params) => api.get('/meetings/', { params }),
  get: (id) => api.get(`/meetings/${id}/`),
  create: (data) => {
    if (data.minutes_file && data.minutes_file instanceof File) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === 'minutes_file') {
          formData.append(key, data[key]);
        } else if (key === 'scheduled_at' || key === 'end_time') {
          const val = data[key];
          if (val != null && val !== '') formData.append(key, new Date(val).toISOString());
        } else if (data[key] != null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      return api.post('/meetings/', formData, {
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }],
      });
    }
    return api.post('/meetings/', data);
  },
  update: (id, data) => {
    if (data.minutes_file && data.minutes_file instanceof File) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === 'minutes_file') {
          formData.append(key, data[key]);
        } else if (key === 'scheduled_at' || key === 'end_time') {
          const val = data[key];
          if (val != null && val !== '') formData.append(key, new Date(val).toISOString());
        } else if (data[key] != null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      return api.patch(`/meetings/${id}/`, formData, {
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }],
      });
    }
    return api.patch(`/meetings/${id}/`, data);
  },
  delete: (id) => api.delete(`/meetings/${id}/`),
  uploadMinutesFile: (id, file) => {
    const formData = new FormData();
    formData.append('minutes_file', file);
    return api.post(`/meetings/${id}/upload-minutes/`, formData, {
      transformRequest: [(data, headers) => {
        delete headers['Content-Type'];
        return data;
      }],
    });
  },
  downloadMinutes: (id) => api.get(`/meetings/${id}/download-minutes/`, { responseType: 'blob' }),
  markAttendance: (id, data) => api.post(`/meetings/${id}/attendance/`, data),
  giveNotice: (id, data) => api.post(`/meetings/${id}/notice/`, data),
  approve: (id) => api.post(`/meetings/${id}/approve/`),
  reopenMinutes: (id) => api.post(`/meetings/${id}/reopen-minutes/`),
  useDraft: (id) => api.post(`/meetings/${id}/use-draft/`),
  addItem: (id, data) => api.post(`/meetings/${id}/items/`, data),
  updateItem: (id, itemId, data) => api.patch(`/meetings/${id}/items/${itemId}/`, data),
  deleteItem: (id, itemId) => api.delete(`/meetings/${id}/items/${itemId}/`),
  makeTask: (id, itemId, data = {}) => api.post(`/meetings/${id}/items/${itemId}/make-task/`, data),
  recordingAudio: (id) => api.get(`/meetings/${id}/recording/audio/`, { responseType: 'blob' }),
  recordingTranscript: (id) => api.get(`/meetings/${id}/recording/transcript/`, { responseType: 'text' }),
  retryRecording: (id, retranscribe = false) => api.post(`/meetings/${id}/recording/retry/`, { retranscribe }),
  deleteRecording: (id) => api.post(`/meetings/${id}/recording/delete/`),
  // Recordings go up in 8 MB parts (nginx caps a request at 12 MB). Each part says where it starts,
  // so a dropped connection resumes from what the server already has instead of starting again.
  uploadRecording: async (id, file, { onProgress } = {}) => {
    const base = `/meetings/${id}/recording`;
    const start = await api.post(`${base}/start/`, { filename: file.name, size: file.size, consent: true });
    const chunkSize = start.data.chunk_size || 8 * 1024 * 1024;
    let offset = start.data.received || 0;
    let failures = 0;
    while (offset < file.size) {
      const form = new FormData();
      form.append('offset', String(offset));
      form.append('chunk', file.slice(offset, offset + chunkSize), 'part');
      try {
        const res = await api.post(`${base}/chunk/`, form, {
          transformRequest: [(data, headers) => { delete headers['Content-Type']; return data; }],
        });
        offset = res.data.received;
        failures = 0;
        if (onProgress) onProgress(offset / file.size);
      } catch (err) {
        if (err.response?.status === 409 && typeof err.response.data?.received === 'number') {
          offset = err.response.data.received;
        } else {
          failures += 1;
          if (failures > 4) throw err;
          await new Promise((r) => setTimeout(r, 2000 * failures));
        }
      }
    }
    return api.post(`${base}/finish/`);
  },
};

export const meetingFlagsAPI = {
  list: (params) => api.get('/meeting-flags/', { params }),
  decide: (id, decision, note) => api.post(`/meeting-flags/${id}/decide/`, { decision, note }),
};

// Team tasks (Foundation Q5): one Accountable, one or more Responsible, logged due-date changes.
export const teamTasksAPI = {
  list: (params) => api.get('/team-tasks/', { params }),
  get: (id) => api.get(`/team-tasks/${id}/`),
  create: (data) => api.post('/team-tasks/', data),
  update: (id, data) => api.patch(`/team-tasks/${id}/`, data),
  setStatus: (id, data) => api.post(`/team-tasks/${id}/status/`, data),
  moveDue: (id, data) => api.post(`/team-tasks/${id}/due-date/`, data),
  reopen: (id, note) => api.post(`/team-tasks/${id}/reopen/`, { note }),
  cancel: (id, reason) => api.post(`/team-tasks/${id}/cancel/`, { reason }),
  comment: (id, note) => api.post(`/team-tasks/${id}/comment/`, { note }),
  people: () => api.get('/team-tasks/people/'),
};

// Mobile content API (Student App Admin – CRUD for app sections)
export const mobileContentAPI = {
  // Quest Map
  questStages: {
    list: (params) => api.get('/quest-stages/', { params }),
    get: (id) => api.get(`/quest-stages/${id}/`),
    create: (data) => api.post('/quest-stages/', data),
    update: (id, data) => api.patch(`/quest-stages/${id}/`, data),
    delete: (id) => api.delete(`/quest-stages/${id}/`),
  },
  // Survival Kit
  packingItems: {
    list: (params) => api.get('/packing-items/', { params }),
    get: (id) => api.get(`/packing-items/${id}/`),
    create: (data) => api.post('/packing-items/', data),
    update: (id, data) => api.patch(`/packing-items/${id}/`, data),
    delete: (id) => api.delete(`/packing-items/${id}/`),
  },
  survivalConfig: {
    get: () => api.get('/survival-config/'),
    update: (data) => api.patch('/survival-config/1/', data),
  },
  // Special Opportunities
  specialOpportunities: {
    list: (params) => api.get('/special-opportunities/', { params }),
    get: (id) => api.get(`/special-opportunities/${id}/`),
    create: (data) => api.post('/special-opportunities/', data),
    update: (id, data) => api.patch(`/special-opportunities/${id}/`, data),
    delete: (id) => api.delete(`/special-opportunities/${id}/`),
  },
  // Delegations (CEO/COO manage who else can access mobile admin)
  delegations: {
    list: (params) => api.get('/delegations/', { params }),
    create: (data) => api.post('/delegations/', data),
    update: (id, data) => api.patch(`/delegations/${id}/`, data),
    delete: (id) => api.delete(`/delegations/${id}/`),
    myAccess: () => api.get('/delegations/my_access/'),
  },
  // Cost of Living Calculator
  costCities: {
    list: (params) => api.get('/cost-cities/', { params }),
    get: (id) => api.get(`/cost-cities/${id}/`),
    create: (data) => api.post('/cost-cities/', data),
    update: (id, data) => api.patch(`/cost-cities/${id}/`, data),
    delete: (id) => api.delete(`/cost-cities/${id}/`),
  },
  costCategories: {
    list: (params) => api.get('/cost-categories/', { params }),
    get: (id) => api.get(`/cost-categories/${id}/`),
    create: (data) => api.post('/cost-categories/', data),
    update: (id, data) => api.patch(`/cost-categories/${id}/`, data),
    delete: (id) => api.delete(`/cost-categories/${id}/`),
  },
  costItems: {
    list: (params) => api.get('/cost-items/', { params }),
    get: (id) => api.get(`/cost-items/${id}/`),
    create: (data) => api.post('/cost-items/', data),
    update: (id, data) => api.patch(`/cost-items/${id}/`, data),
    delete: (id) => api.delete(`/cost-items/${id}/`),
  },
  costPrices: {
    list: (params) => api.get('/cost-prices/', { params }),
    get: (id) => api.get(`/cost-prices/${id}/`),
    create: (data) => api.post('/cost-prices/', data),
    update: (id, data) => api.patch(`/cost-prices/${id}/`, data),
    delete: (id) => api.delete(`/cost-prices/${id}/`),
    bulkUpdate: (prices) => api.post('/cost-prices/bulk_update/', { prices }),
  },
};

// Support Inbox API (staff-facing — student in-app support chat)
// App Leads — lightweight leads captured from the VisionExploit app
// (consultation / WhatsApp callback / eligibility quiz), Phase 1.5/1.6.
export const appLeadsAPI = {
  list:          (params) => api.get('/app/leads/', { params }),
  // Overdue count + this month's CSM1 first-response %, for one source (APP/WEBSITE).
  responseStats: (source) => api.get('/app/leads/response_stats/', { params: { source } }),
  activity:      (id)     => api.get(`/app/leads/${id}/activity/`),
  markContacted: (id)     => api.post(`/app/leads/${id}/mark_contacted/`),
  dismiss:       (id, reason) => api.post(`/app/leads/${id}/dismiss/`, { reason }),
  convert:         (id, body) => api.post(`/app/leads/${id}/convert/`, body),
  convertToJob:    (id, body) => api.post(`/app/leads/${id}/convert-to-job/`, body),
  convertToTourist: (id, body) => api.post(`/app/leads/${id}/convert-to-tourist/`, body),
  // Marketing channel (TikTok, Instagram…), paid/organic and campaign — CMO3.
  setSource: (id, body) => api.post(`/app/leads/${id}/set-source/`, body),
  // Follow-up ticks: CALLED, WHATSAPP, INFO_SENT, CONSULT_BOOKED, CONSULT_HELD.
  step: (id, step, done) => api.post(`/app/leads/${id}/step/`, { step, done }),
};

// The CMO's marketing plan, weekly report and results (marketing app).
export const marketingAPI = {
  overview: (month) => api.get('/marketing/overview/', { params: { month } }),
  createPlan: (body) => api.post('/marketing/plans/', body),
  updatePlan: (id, body) => api.patch(`/marketing/plans/${id}/`, body),
  createItem: (body) => api.post('/marketing/items/', body),
  updateItem: (id, body) => api.patch(`/marketing/items/${id}/`, body),
  deleteItem: (id) => api.delete(`/marketing/items/${id}/`),
  markItem: (id, body) => api.post(`/marketing/items/${id}/mark/`, body),
  itemMetrics: (id, rows) => api.put(`/marketing/items/${id}/metrics/`, rows),
  campaigns: (params) => api.get('/marketing/campaigns/', { params }),
  createCampaign: (body) => api.post('/marketing/campaigns/', body),
  updateCampaign: (id, body) => api.patch(`/marketing/campaigns/${id}/`, body),
  deleteCampaign: (id) => api.delete(`/marketing/campaigns/${id}/`),
  createSeries: (body) => api.post('/marketing/series/', body),
  updateSeries: (id, body) => api.patch(`/marketing/series/${id}/`, body),
  stopSeries: (id) => api.delete(`/marketing/series/${id}/`),
  comments: (params) => api.get('/marketing/comments/', { params }),
  addComment: (body) => api.post('/marketing/comments/', body),
  weekly: (week) => api.get('/marketing/weekly/', { params: { week } }),
  saveWeekly: (body) => api.post('/marketing/weekly/', body),
  submitWeekly: (id) => api.post(`/marketing/weekly/${id}/submit/`),
  uploadScreenshot: (id, platform, file) => {
    const form = new FormData();
    form.append('platform', platform);
    form.append('file', file);
    return api.post(`/marketing/weekly/${id}/screenshot/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Facebook / Instagram posts read from Meta that no plan item points at.
  attachSocialPost: (id, item) => api.post(`/marketing/social-posts/${id}/attach/`, { item }),
  extraSocialPost: (id) => api.post(`/marketing/social-posts/${id}/extra/`),
  tiktokConnect: () => api.get('/marketing/tiktok/connect/'),
  tiktokCallback: (body) => api.post('/marketing/tiktok/callback/', body),
  tiktokDisconnect: () => api.post('/marketing/tiktok/disconnect/'),
};

// Role duty routines and staff-only client checklists (checklists app).
export const dutiesAPI = {
  mine: () => api.get('/duties/mine/'),
  list: (params) => api.get('/duties/', { params }),
  create: (body) => api.post('/duties/', body),
  update: (id, body) => api.patch(`/duties/${id}/`, body),
  retire: (id) => api.delete(`/duties/${id}/`),
  tick: (id, periodStart, note) => api.post(`/duties/${id}/tick/`, { period_start: periodStart, note }),
  untick: (id, periodStart) => api.post(`/duties/${id}/untick/`, { period_start: periodStart }),
};

export const clientStepsAPI = {
  get: (kind, id) => api.get('/client-steps/', { params: { kind, id } }),
  tick: (kind, id, template, done) => api.post('/client-steps/tick/', { kind, id, template, done }),
};

// App conversion funnel — installs -> engaged -> lead -> consultation ->
// converted, for the trailing window (Phase 1.5 metrics).
export const appFunnelAPI = {
  get: (days = 30) => api.get('/app/funnel/', { params: { days } }),
};

export const feedbackAPI = {
  list:          (params)   => api.get('/feedback/', { params }),
  summary:       (params)   => api.get('/feedback/summary/', { params }),
  create:        (body)     => api.post('/feedback/', body),
  markReviewed:  (id, body) => api.post(`/feedback/${id}/mark_reviewed/`, body),
  searchClients: (params)   => api.get('/feedback/client-search/', { params }),
};

// Generic visa document tracker (visa_cases app): any client kind, any of the
// 12 visa types, mission-specific templates.
// Journey, consent, pre-visa document checklist and client link for job seekers and medical clients
// (client_journeys app). kind is 'job' or 'medical'.
const cj = (kind, id) => `/client-journeys/${kind}/${id}/`;
export const clientJourneysAPI = {
  get:        (kind, id)            => api.get(cj(kind, id)),
  consent:    (kind, id, body)      => api.post(cj(kind, id) + 'consent/', body),
  link:       (kind, id)            => api.get(cj(kind, id) + 'link/'),
  linkOp:     (kind, id, op)        => api.post(cj(kind, id) + `link/${op}/`),
  itemOp:     (kind, id, key, op, body = {}) => api.post(cj(kind, id) + `items/${key}/${op}/`, body),
  upload:     (kind, id, key, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(cj(kind, id) + `items/${key}/upload/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  file:       (kind, id, key, which) => api.get(cj(kind, id) + `items/${key}/file/`, { params: { which }, responseType: 'blob' }),
  payments:   (id)                  => api.get(`/client-journeys/medical/${id}/payments/`),
  addPayment: (id, body)            => {
    const form = new FormData();
    Object.entries(body).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') form.append(k, v); });
    return api.post(`/client-journeys/medical/${id}/payments/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  paymentOp:  (id, pid, op, body = {}) => api.post(`/client-journeys/medical/${id}/payments/${pid}/${op}/`, body),
  receipt:    (id, pid)             => api.get(`/client-journeys/medical/${id}/payments/${pid}/receipt/`, { responseType: 'blob' }),
};

export const visaCasesAPI = {
  list:          (params)     => api.get('/visa-cases/', { params }),
  get:           (id)         => api.get(`/visa-cases/${id}/`),
  create:        (body)       => api.post('/visa-cases/', body),
  update:        (id, body)   => api.patch(`/visa-cases/${id}/`, body),
  items:         (id)         => api.get(`/visa-cases/${id}/items/`),
  catalog:       ()           => api.get('/visa-cases/catalog/'),
  dashboard:     ()           => api.get('/visa-cases/dashboard/'),
  routeCheck:    (params)     => api.get('/visa-cases/route-check/', { params }),
  searchClients: (params)     => api.get('/visa-cases/client-search/', { params }),
  link:          (id)         => api.get(`/visa-cases/${id}/link/`),
  regenerateLink:(id)         => api.post(`/visa-cases/${id}/link/regenerate/`),
  revokeLink:    (id)         => api.post(`/visa-cases/${id}/link/revoke/`),
  accept:        (itemId, body) => api.post(`/visa-case-items/${itemId}/accept/`, body),
  reject:        (itemId, note, upload_version) => api.post(`/visa-case-items/${itemId}/reject/`, { note, upload_version }),
  // Files are fetched through the API as a download (never a raw /media/ link) so they carry
  // the staff login and are served with safe headers.
  downloadFile:  (itemId, which) => api.get(`/visa-case-items/${itemId}/file/`, { params: { which }, responseType: 'blob' }),
  // Branded client checklist PDF for one visa type at one mission (built from the live template).
  checklistPdf:  (templateId) => api.get(`/visa-cases/checklist-pdf/${templateId}/`, { responseType: 'blob' }),
  notNeeded:     (itemId)     => api.post(`/visa-case-items/${itemId}/mark-not-needed/`),
  reopen:        (itemId)     => api.post(`/visa-case-items/${itemId}/reopen/`),
  confirm:       (itemId)     => api.post(`/visa-case-items/${itemId}/confirm/`),
  upload:        (itemId, file, extra = {}) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(extra).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') form.append(k, v); });
    return api.post(`/visa-case-items/${itemId}/upload/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Arrival & settling (Customer Support Officers). Assign / close / territories: admin tier only.
export const settlingAPI = {
  board:         ()           => api.get('/settling/board/'),
  list:          (params)     => api.get('/settling/cases/', { params }),
  get:           (id)         => api.get(`/settling/cases/${id}/`),
  open:          (client_kind, client_id) => api.post('/settling/cases/', { client_kind, client_id }),
  update:        (id, body)   => api.patch(`/settling/cases/${id}/`, body),
  assign:        (id, sso)    => api.post(`/settling/cases/${id}/assign/`, { sso }),
  finish:        (id, status) => api.post(`/settling/cases/${id}/finish/`, { status }),
  markArrived:   (id)         => api.post(`/settling/cases/${id}/mark-arrived/`),
  addNote:       (id, body, escalate = false) => api.post(`/settling/cases/${id}/notes/`, { body, escalate }),
  resolveNote:   (noteId)     => api.post(`/settling/notes/${noteId}/resolve/`),
  updateItem:    (itemId, body) => api.patch(`/settling/items/${itemId}/`, body),
  searchClients: (params)     => api.get('/settling/client-search/', { params }),
  officers:      ()           => api.get('/settling/officers/'),
  updateProfile: (id, body)   => api.patch(`/settling/officers/${id}/profile/`, body),
  territories:   ()           => api.get('/settling/territories/'),
  addTerritory:  (body)       => api.post('/settling/territories/', body),
  updateTerritory: (id, body) => api.patch(`/settling/territories/${id}/`, body),
  deleteTerritory: (id)       => api.delete(`/settling/territories/${id}/`),
  checkIns:      (params)     => api.get('/settling/check-ins/', { params }),
  recordCheckIn: (id, body)   => api.post(`/settling/check-ins/${id}/record/`, body),
  termDates:     ()           => api.get('/settling/term-dates/'),
  addTermDate:   (body)       => api.post('/settling/term-dates/', body),
  updateTermDate: (id, body)  => api.patch(`/settling/term-dates/${id}/`, body),
  deleteTermDate: (id)        => api.delete(`/settling/term-dates/${id}/`),
};

export const supportAPI = {
  listTickets: (params) => api.get('/community/support/tickets/', { params }),
  getTicket:   (id)     => api.get(`/community/support/tickets/${id}/`),
  reply:       (id, body) => api.post(`/community/support/tickets/${id}/reply/`, { body }),
  close:       (id)     => api.post(`/community/support/tickets/${id}/close/`),
  reopen:      (id)     => api.post(`/community/support/tickets/${id}/reopen/`),
  markRead:    (id)     => api.post(`/community/support/tickets/${id}/read/`),
};

// Home: what needs each team member today (replaced the Executive Dashboard, Oct 2026)
export const homeAPI = {
  get: (period) => api.get('/home/', { params: { period } }),
};

// Service Orders API (monetized add-ons per student — translation, insurance, İkamet, housing)
export const serviceOrdersAPI = {
  list: (params) => api.get('/service-orders/', { params }),
  create: (data) => api.post('/service-orders/', data),
  update: (id, data) => api.patch(`/service-orders/${id}/`, data),
  delete: (id) => api.delete(`/service-orders/${id}/`),
  revenueSummary: () => api.get('/service-orders/revenue_summary/'),
  vendors: {
    list: () => api.get('/service-vendors/'),
    create: (data) => api.post('/service-vendors/', data),
    update: (id, data) => api.patch(`/service-vendors/${id}/`, data),
    delete: (id) => api.delete(`/service-vendors/${id}/`),
  },
};

// Medical Tourism API (second client vertical, alongside Students — same
// Bearer-token auth and DRF pagination shape as studentsAPI/serviceOrdersAPI)
export const medicalTourismAPI = {
  list: (params) => api.get('/medical-clients/', { params }),
  get: (id) => api.get(`/medical-clients/${id}/`),
  create: (data) => api.post('/medical-clients/', data),
  update: (id, data) => api.patch(`/medical-clients/${id}/`, data),
  delete: (id) => api.delete(`/medical-clients/${id}/`),
  ban: (id, reason) => api.post(`/medical-clients/${id}/ban/`, { reason }),
  unban: (id) => api.post(`/medical-clients/${id}/unban/`),
  issueLoginSetup: (id) => api.post(`/medical-clients/${id}/issue-login-setup/`),
  documents: {
    list: (clientId) => api.get('/medical-documents/', { params: { client: clientId } }),
    create: (data) => {
      const formData = new FormData();
      formData.append('client', data.client);
      formData.append('document_type', data.document_type);
      formData.append('file', data.file);
      return api.post('/medical-documents/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
  },
  progressLogs: {
    list: (clientId) => api.get('/medical-progress-logs/', { params: { client: clientId } }),
    create: (data) => api.post('/medical-progress-logs/', data),
  },
};

// Job Seekers API (work-abroad client vertical — never mixed with Students)
export const jobClientsAPI = {
  list: (params) => api.get('/job-clients/', { params }),
  get: (id) => api.get(`/job-clients/${id}/`),
  create: (data) => api.post('/job-clients/', data),
  update: (id, data) => api.patch(`/job-clients/${id}/`, data),
  delete: (id) => api.delete(`/job-clients/${id}/`),
  readiness: (id) => api.get(`/job-clients/${id}/readiness/`),
  issueLoginSetup: (id) => api.post(`/job-clients/${id}/issue-login-setup/`),
};

// Job vertical operations (staff only): destinations, employers, vacancies,
// verification, consent, money, aftercare. See job_clients/urls.py.
export const jobVerticalAPI = {
  destinations: {
    list: () => api.get('/job-destinations/', { params: { page_size: 200 } }),
    create: (data) => api.post('/job-destinations/', data),
    update: (id, data) => api.patch(`/job-destinations/${id}/`, data),
  },
  requirements: {
    create: (data) => api.post('/job-requirements/', data),
    update: (id, data) => api.patch(`/job-requirements/${id}/`, data),
    delete: (id) => api.delete(`/job-requirements/${id}/`),
  },
  employers: {
    list: (params) => api.get('/job-employers/', { params: { page_size: 200, ...params } }),
    create: (data) => api.post('/job-employers/', data),
    update: (id, data) => api.patch(`/job-employers/${id}/`, data),
    verify: (id, note) => api.post(`/job-employers/${id}/verify/`, { note }),
    suspend: (id, reason) => api.post(`/job-employers/${id}/suspend/`, { reason }),
  },
  vacancies: {
    list: (params) => api.get('/job-vacancies/', { params: { page_size: 200, ...params } }),
    create: (data) => api.post('/job-vacancies/', data),
    update: (id, data) => api.patch(`/job-vacancies/${id}/`, data),
  },
  checks: {
    create: (data) => api.post('/job-checks/', data),
    delete: (id) => api.delete(`/job-checks/${id}/`),
  },
  consents: {
    create: (data) => api.post('/job-consents/', data),
    withdraw: (id) => api.post(`/job-consents/${id}/withdraw/`),
    disclose: (id) => api.post(`/job-consents/${id}/disclose/`),
  },
  fees: {
    list: (params) => api.get('/job-fees/', { params }),
    create: (data) => api.post('/job-fees/', data),
    update: (id, data) => api.patch(`/job-fees/${id}/`, data),
  },
  expenses: {
    create: (data) => api.post('/job-expenses/', data),
  },
  checkins: {
    list: (params) => api.get('/job-checkins/', { params: { page_size: 200, ...params } }),
    complete: (id, data) => api.post(`/job-checkins/${id}/complete/`, data),
  },
  feeSchedule: {
    list: (params) => api.get('/job-fee-schedule/', { params: { page_size: 200, ...params } }),
    create: (data) => api.post('/job-fee-schedule/', data),
    update: (id, data) => api.patch(`/job-fee-schedule/${id}/`, data),
    remove: (id) => api.delete(`/job-fee-schedule/${id}/`),
  },
  charges: {
    list: (params) => api.get('/job-charges/', { params: { page_size: 200, ...params } }),
    create: (data) => api.post('/job-charges/', data),
    disclose: (id, method) => api.post(`/job-charges/${id}/disclose/`, { method }),
    acknowledge: (id, method) => api.post(`/job-charges/${id}/acknowledge/`, { method }),
    pay: (id, data) => api.post(`/job-charges/${id}/pay/`, data),
    refund: (id, data) => api.post(`/job-charges/${id}/refund/`, data),
    waive: (id, reason) => api.post(`/job-charges/${id}/waive/`, { reason }),
  },
  incidents: {
    list: (params) => api.get('/job-incidents/', { params: { page_size: 200, ...params } }),
    create: (data) => api.post('/job-incidents/', data),
    resolve: (id, resolution) => api.post(`/job-incidents/${id}/resolve/`, { resolution }),
  },
};

// Flatten a DRF error body into one readable string.
export const jobApiError = (e, fallback = 'Something went wrong.') => {
  const d = e?.response?.data;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d.blockers)) return d.blockers.join(' ');
  const parts = [];
  const walk = (v) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') Object.values(v).forEach(walk);
    else if (v != null) parts.push(String(v));
  };
  walk(d);
  return parts.join(' ') || fallback;
};

// Tourists API (normal-tourism client vertical, separate from Medical Tourism)
export const touristClientsAPI = {
  list: (params) => api.get('/tourist-clients/', { params }),
  get: (id) => api.get(`/tourist-clients/${id}/`),
  create: (data) => api.post('/tourist-clients/', data),
  update: (id, data) => api.patch(`/tourist-clients/${id}/`, data),
  delete: (id) => api.delete(`/tourist-clients/${id}/`),
  issueLoginSetup: (id) => api.post(`/tourist-clients/${id}/issue-login-setup/`),
};

// Financial Management API (Chairman/CFO + grant-holders — MFA-gated, see FinanceMFAGate)
export const financeAPI = {
  // Cheap "do I have finance access" check for grant-holders (role-based leadership always does) —
  // used to decide whether the Finance nav item should show, mirroring mobileContentAPI.delegations.myAccess.
  myAccess: () => api.get('/finance/my-access/'),
  mfa: {
    status:    () => api.get('/finance/mfa/status/'),
    provision: () => api.post('/finance/mfa/provision/'),
    confirm:   (code) => api.post('/finance/mfa/confirm/', { code }),
    verify:    (code) => api.post('/finance/mfa/verify/', { code }),
  },
  entities: {
    list: () => api.get('/finance/entities/'),
  },
  currencies: {
    list: () => api.get('/finance/currencies/'),
  },
  accounts: {
    list:   (params) => api.get('/finance/accounts/', { params }),
    get:    (id) => api.get(`/finance/accounts/${id}/`),
    create: (data) => api.post('/finance/accounts/', data),
    update: (id, data) => api.patch(`/finance/accounts/${id}/`, data),
    openItems: (id, params) => api.get(`/finance/accounts/${id}/open-items/`, { params }),
  },
  fxRates: {
    list:   (params) => api.get('/finance/fx-rates/', { params }),
    create: (data) => api.post('/finance/fx-rates/', data),
  },
  periods: {
    list:   (params) => api.get('/finance/periods/', { params }),
    create: (data) => api.post('/finance/periods/', data),
    closeChecklist: (id) => api.get(`/finance/periods/${id}/close-checklist/`),
    close:  (id) => api.post(`/finance/periods/${id}/close/`, {}),
    trialBalance:      (id, params) => api.get(`/finance/periods/${id}/trial-balance/`, { params }),
    incomeStatement:   (id, params) => api.get(`/finance/periods/${id}/income-statement/`, { params }),
    balanceSheet:      (id, params) => api.get(`/finance/periods/${id}/balance-sheet/`, { params }),
    cashFlowStatement: (id, params) => api.get(`/finance/periods/${id}/cash-flow-statement/`, { params }),
  },
  periodReopenRequests: {
    list:   (params) => api.get('/finance/period-reopen-requests/', { params }),
    create: (data) => api.post('/finance/period-reopen-requests/', data),
    approveChairman: (id, mfaCode) => api.post(`/finance/period-reopen-requests/${id}/approve-chairman/`, { mfa_code: mfaCode }),
    approveCfo:      (id, mfaCode) => api.post(`/finance/period-reopen-requests/${id}/approve-cfo/`, { mfa_code: mfaCode }),
    reject: (id, mfaCode) => api.post(`/finance/period-reopen-requests/${id}/reject/`, { mfa_code: mfaCode }),
  },
  recurringTemplates: {
    list:       (params) => api.get('/finance/recurring-templates/', { params }),
    create:     (data) => api.post('/finance/recurring-templates/', data),
    generateNow: (id) => api.post(`/finance/recurring-templates/${id}/generate-now/`, {}),
  },
  entries: {
    list:    (params) => api.get('/finance/entries/', { params }),
    get:     (id) => api.get(`/finance/entries/${id}/`),
    create:  (data) => api.post('/finance/entries/', data),
    post:    (id) => api.post(`/finance/entries/${id}/post/`, {}),
    reverse: (id, memo) => api.post(`/finance/entries/${id}/reverse/`, { memo }),
    void:    (id) => api.post(`/finance/entries/${id}/void/`, {}),
    // Guided entry for a CFO without accounting background — see
    // VEC_MS_FINANCE_ENTRY_GUIDE.md and finance/quick_entry.py.
    quickEntry:       (data) => api.post('/finance/entries/quick-entry/', data),
    suggestCategory:  (description, direction) =>
      api.post('/finance/entries/suggest-category/', { description, direction }),
    // Cash-holder transfers — see finance/cash_transfer.py and
    // brainstorms/2026-09-22-cash-holder-tracking-discovery.md.
    transferCash:  (data) => api.post('/finance/entries/transfer-cash/', data),
    cashHolders:   (entityCode) => api.get('/finance/entries/cash-holders/', { params: { entity_code: entityCode } }),
  },
  // CAC/LTV/ROI/cash-position/cost-of-time + daily-weekly-monthly-quarterly-
  // yearly rollups, in one call. entity is the numeric LegalEntity id.
  dashboard: (entityId, periodName) =>
    api.get('/finance/dashboard/', { params: { entity: entityId, period_name: periodName || undefined } }),
  expenses: {
    list:      (params) => api.get('/finance/expenses/', { params }),
    get:       (id) => api.get(`/finance/expenses/${id}/`),
    create:    (data) => api.post('/finance/expenses/', data),
    submit:    (id) => api.post(`/finance/expenses/${id}/submit/`, {}),
    approve:   (id) => api.post(`/finance/expenses/${id}/approve/`, {}),
    reject:    (id, reason) => api.post(`/finance/expenses/${id}/reject/`, { reason }),
    addReceipt: (id, file) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(`/finance/expenses/${id}/add_receipt/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
  },
  grants: {
    list:   () => api.get('/finance/access-grants/'),
    create: (data) => api.post('/finance/access-grants/', data),
    revoke: (id, reason) => api.post(`/finance/access-grants/${id}/revoke/`, { reason }),
  },
  auditLog: {
    list: (params) => api.get('/finance/audit-log/', { params }),
  },
  bankAccounts: {
    list:   (params) => api.get('/finance/bank-accounts/', { params }),
    create: (data) => api.post('/finance/bank-accounts/', data),
    importStatement: (id, file) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(`/finance/bank-accounts/${id}/import-statement/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    reconciliation: (id, start, end) => api.get(`/finance/bank-accounts/${id}/reconciliation/`, { params: { start, end } }),
  },
  bankStatementLines: {
    list:    (params) => api.get('/finance/bank-statement-lines/', { params }),
    match:   (id, journalLineId) => api.post(`/finance/bank-statement-lines/${id}/match/`, { journal_line_id: journalLineId }),
    unmatch: (id) => api.post(`/finance/bank-statement-lines/${id}/unmatch/`, {}),
  },
  salesInvoices: {
    list:   (params) => api.get('/finance/sales-invoices/', { params }),
    create: (data) => api.post('/finance/sales-invoices/', data),
    issue:  (id) => api.post(`/finance/sales-invoices/${id}/issue/`, {}),
    cancel: (id, reason) => api.post(`/finance/sales-invoices/${id}/cancel/`, { reason }),
  },
  intercompany: {
    list:   () => api.get('/finance/intercompany-transactions/'),
    create: (data) => api.post('/finance/intercompany-transactions/', data),
    approveChairman: (id, mfaCode) => api.post(`/finance/intercompany-transactions/${id}/approve-chairman/`, { mfa_code: mfaCode }),
    approveCfo:      (id, mfaCode) => api.post(`/finance/intercompany-transactions/${id}/approve-cfo/`, { mfa_code: mfaCode }),
    reject: (id, reason, mfaCode) => api.post(`/finance/intercompany-transactions/${id}/reject/`, { reason, mfa_code: mfaCode }),
  },
  taxDeadlines: {
    list:     (params) => api.get('/finance/tax-deadlines/', { params }),
    create:   (data) => api.post('/finance/tax-deadlines/', data),
    upcoming: (withinDays) => api.get('/finance/tax-deadlines/upcoming/', { params: { within_days: withinDays } }),
    complete: (id, notes) => api.post(`/finance/tax-deadlines/${id}/complete/`, { notes }),
  },
  zimbabweTaxEstimates: {
    list:      (params) => api.get('/finance/zimbabwe-tax-estimates/', { params }),
    create:    (data) => api.post('/finance/zimbabwe-tax-estimates/', data),
    qpdStatus: (id) => api.get(`/finance/zimbabwe-tax-estimates/${id}/qpd-status/`),
  },
  zimbabweQpdPayments: {
    list:          (params) => api.get('/finance/zimbabwe-qpd-payments/', { params }),
    recordPayment: (id, data) => api.post(`/finance/zimbabwe-qpd-payments/${id}/record-payment/`, data),
  },
  anomalyFlags: {
    list:     (params) => api.get('/finance/anomaly-flags/', { params }),
    review:   (id, notes) => api.post(`/finance/anomaly-flags/${id}/review/`, { notes }),
    runScans: (entity, period) => api.post('/finance/anomaly-flags/run-scans/', { entity, period }),
  },
  auditorExports: {
    list:   () => api.get('/finance/auditor-exports/'),
    create: (data, mfaCode) => api.post('/finance/auditor-exports/', { ...data, mfa_code: mfaCode }),
    revoke: (id) => api.post(`/finance/auditor-exports/${id}/revoke/`, {}),
  },
  excelMigrations: {
    list:    () => api.get('/finance/excel-migrations/'),
    signOff: (id, notes) => api.post(`/finance/excel-migrations/${id}/sign-off/`, { notes }),
  },
  paymentProviderMappings: {
    list:   () => api.get('/finance/payment-provider-mappings/'),
    create: (data) => api.post('/finance/payment-provider-mappings/', data),
    update: (id, data) => api.patch(`/finance/payment-provider-mappings/${id}/`, data),
  },
  paymentTransactions: {
    list: (params) => api.get('/finance/payment-transactions/', { params }),
  },
  paymentPayouts: {
    list:      () => api.get('/finance/payment-payouts/'),
    reconcile: (providerName, providerPayoutId, entity) =>
      api.post('/finance/payment-payouts/reconcile/', { provider_name: providerName, provider_payout_id: providerPayoutId, entity }),
  },
};

export default api;


// Language lessons (Turkish) -- see backend lessons/urls.py.
export const lessonsAPI = {
  overview:        ()            => api.get('/lessons/overview/'),
  curriculum:      ()            => api.get('/lessons/curriculum/'),
  taughtHours:     (params)      => api.get('/lessons/taught-hours/', { params }),
  summary:         (kind, id)    => api.get(`/lessons/summary/${kind}/${id}/`),

  teachers:        ()            => api.get('/lessons/teachers/'),
  createTeacher:   (body)        => api.post('/lessons/teachers/', body),
  updateTeacher:   (id, body)    => api.patch(`/lessons/teachers/${id}/`, body),
  teacherLink:     (id)          => api.post(`/lessons/teachers/${id}/`),

  createWalkin:    (body)        => api.post('/lessons/walkins/', body),
  walkins:         (params)      => api.get('/lessons/walkins/', { params }),
  walkin:          (id)          => api.get(`/lessons/walkins/${id}/`),
  updateWalkin:    (id, body)    => api.patch(`/lessons/walkins/${id}/`, body),
  clientSearch:    (q)           => api.get('/lessons/client-search/', { params: { q } }),
  duplicateCheck:  (params)      => api.get('/lessons/duplicate-check/', { params }),
  duplicates:      ()            => api.get('/lessons/duplicates/'),
  linkClient:      (id, body)    => api.post(`/lessons/learners/${id}/link-client/`, body),
  assignTeacher:   (id, body)    => api.post(`/lessons/learners/${id}/assign-teacher/`, body),
  walkinAppAccess: (id)          => api.post(`/lessons/walkins/${id}/`),

  learners:        (params)      => api.get('/lessons/learners/', { params }),
  learner:         (id)          => api.get(`/lessons/learners/${id}/`),
  startLearner:    (body)        => api.post('/lessons/learners/', body),
  updateLearner:   (id, body)    => api.patch(`/lessons/learners/${id}/`, body),
  sellPackage:     (id, body)    => api.post(`/lessons/learners/${id}/packages/`, body),
  packageAction:   (id, op, body) => api.post(`/lessons/packages/${id}/${op}/`, body || {}),
  packageTemplates: ()           => api.get('/lessons/package-templates/'),
  learnerLink:     (id)          => api.get(`/lessons/learners/${id}/link/`),
  learnerLinkOp:   (id, op)      => api.post(`/lessons/learners/${id}/link/${op}/`),

  classes:         ()            => api.get('/lessons/classes/'),
  classDetail:     (id)          => api.get(`/lessons/classes/${id}/`),
  createClass:     (body)        => api.post('/lessons/classes/', body),
  updateClass:     (id, body)    => api.patch(`/lessons/classes/${id}/`, body),
  classAction:     (id, op, body) => api.post(`/lessons/classes/${id}/${op}/`, body || {}),

  lessons:         (params)      => api.get('/lessons/lessons/', { params }),
  lesson:          (id)          => api.get(`/lessons/lessons/${id}/`),
  lessonAction:    (id, op, body) => api.post(`/lessons/lessons/${id}/${op}/`, body || {}),
  changes:         (params)      => api.get('/lessons/changes/', { params }),
  undoChange:      (id)          => api.post(`/lessons/changes/${id}/undo/`),

  blockedDates:    ()            => api.get('/lessons/blocked-dates/'),
  addBlockedDate:  (body)        => api.post('/lessons/blocked-dates/', body),
  removeBlockedDate: (id)        => api.delete(`/lessons/blocked-dates/${id}/`),
  levelChanges:    (params)      => api.get('/lessons/level-changes/', { params }),
  decideLevel:     (id, op, body) => api.post(`/lessons/level-changes/${id}/${op}/`, body || {}),

  quizzes:         ()            => api.get('/lessons/quizzes/'),
  learnerTests:    (id)          => api.get(`/lessons/learners/${id}/quizzes/`),
  sendTest:        (id, quiz)    => api.post(`/lessons/learners/${id}/quizzes/`, { quiz }),
  cancelTest:      (id)          => api.post(`/lessons/quiz-assignments/${id}/cancel/`),
};

// The Foundation, step 1: team rules + acknowledgement, roles register, agreements.
export const foundationAPI = {
  myRules:            ()            => api.get('/foundation/rules/current/'),
  acknowledge:        (body)        => api.post('/foundation/rules/current/', body),
  rulesVersions:      ()            => api.get('/foundation/rules/'),
  rulesVersion:       (id)          => api.get(`/foundation/rules/${id}/`),
  createRulesVersion: (body)        => api.post('/foundation/rules/', body),
  updateRulesVersion: (id, body)    => api.patch(`/foundation/rules/${id}/`, body),
  publishRules:       (id)          => api.post(`/foundation/rules/${id}/publish/`),
  rulesStatus:        (id)          => api.get(`/foundation/rules/${id}/status/`),
  roles:              ()            => api.get('/foundation/roles/'),
  addHolder:          (roleId, body) => api.post(`/foundation/roles/${roleId}/holders/`, body),
  endHolding:         (id)          => api.post(`/foundation/holders/${id}/end/`),
  people:             ()            => api.get('/foundation/people/'),
  agreements:         (params)      => api.get('/foundation/agreements/', { params }),
  addAgreement:       (body)        => api.post('/foundation/agreements/', body),
  withdrawAgreement:  (id)          => api.post(`/foundation/agreements/${id}/withdraw/`),
  // Step 2
  kpis:               (period)      => api.get('/foundation/kpis/', { params: { period } }),
  myAnalytics:        (period, person) => api.get('/foundation/my-analytics/', { params: { period, person: person || undefined } }),
  kpiReading:         (id, body)    => api.post(`/foundation/kpis/${id}/reading/`, body),
  kpiTarget:          (id, body)    => api.post(`/foundation/kpis/${id}/target/`, body),
  kpiRefresh:         (period)      => api.post('/foundation/kpis/refresh/', { period }),
  gap:                ()            => api.get('/foundation/gap/'),
  openGapRound:       (body)        => api.post('/foundation/gap/', body),
  gapReview:          (id)          => api.get(`/foundation/gap/reviews/${id}/`),
  saveGapReview:      (id, body)    => api.post(`/foundation/gap/reviews/${id}/`, body),
  addGapAction:       (id, body)    => api.post(`/foundation/gap/reviews/${id}/actions/`, body),
  updateGapAction:    (id, body)    => api.patch(`/foundation/gap/actions/${id}/`, body),
  deleteGapAction:    (id)          => api.delete(`/foundation/gap/actions/${id}/`),
  gapSummary:         (id)          => api.get(`/foundation/gap/rounds/${id}/summary/`),
  saveGapSummary:     (id, body)    => api.post(`/foundation/gap/rounds/${id}/summary/`, body),
  complaints:         (show)        => api.get('/foundation/complaints/', { params: { show } }),
  hiring:             ()            => api.get('/foundation/hiring/'),
  openVacancy:        (body)        => api.post('/foundation/hiring/', body),
  vacancyAction:      (id, op, body) => api.post(`/foundation/hiring/vacancies/${id}/${op}/`, body || {}),
  candidate:          (id)          => api.get(`/foundation/hiring/candidates/${id}/`),
  candidateAction:    (id, op, body) => api.post(`/foundation/hiring/candidates/${id}/${op}/`, body || {}),
  probationAction:    (id, op, body) => api.post(`/foundation/hiring/probations/${id}/${op}/`, body || {}),
  recordExit:         (body)        => api.post('/foundation/hiring/exits/', body),
  reviews:            ()            => api.get('/foundation/reviews/'),
  openReview:         (body)        => api.post('/foundation/reviews/', body),
  review:             (id)          => api.get(`/foundation/reviews/${id}/`),
  reviewAction:       (id, op, body) => api.post(`/foundation/reviews/${id}/${op}/`, body || {}),
  decideProposal:     (id, body)    => api.post(`/foundation/proposals/${id}/`, body),
  logComplaint:       (body)        => api.post('/foundation/complaints/', body),
  complaintAction:    (id, op, body) => api.post(`/foundation/complaints/${id}/${op}/`, body || {}),
  conduct:            ()            => api.get('/foundation/conduct/'),
  reportConduct:      (body)        => api.post('/foundation/conduct/', body),
  conductAction:      (id, op, body) => api.post(`/foundation/conduct/${id}/${op}/`, body || {}),
  legalCheck:         (body)        => api.post('/foundation/conduct/legal-check/', body),
  // Q14: the Foundation document (all eight parts) and the RACI chart as data
  document:           ()            => api.get('/foundation/document/'),
  myParts:            ()            => api.get('/foundation/document/my/'),
  part:               (n)           => api.get(`/foundation/document/parts/${n}/`),
  newPartDraft:       (n, body)     => api.post(`/foundation/document/parts/${n}/`, body),
  partVersion:        (id)          => api.get(`/foundation/document/versions/${id}/`),
  updatePartVersion:  (id, body)    => api.patch(`/foundation/document/versions/${id}/`, body),
  publishPartVersion: (id, body)    => api.post(`/foundation/document/versions/${id}/publish/`, body || {}),
  acknowledgePart:    (id, body)    => api.post(`/foundation/document/versions/${id}/acknowledge/`, body),
  partStatus:         (id)          => api.get(`/foundation/document/versions/${id}/status/`),
  exportDocument:     (drafts)      => api.get('/foundation/document/export/', { params: drafts ? { drafts: 1 } : {}, responseType: 'blob' }),
  raci:               ()            => api.get('/foundation/raci/'),
  raciRow:            (code)        => api.get(`/foundation/raci/${encodeURIComponent(code)}/`),
};
